import isBoolean from 'lodash/isBoolean';
import isNil from 'lodash/isNil';

import { RESET_ALL as RESET_ON_LOGOUT } from 'redux-store/reducers/common-reducer';

import { createReducer } from 'utils/redux';
import { API, post } from 'utils/api';
import { logError } from 'utils/log';
import { convertItemToCheckbox, getActiveQuery } from 'modules/insights/sov/utils/queries/sov-queries';
import { getDefaultChannelsMap } from 'modules/insights/sov/utils/queries/channels/sov-queries-channels';

import type {
  Action,
  CheckboxesListItem,
  Dispatch,
  GetState,
  ThunkAction
} from 'types';
import type {
  SOVQueryChannelsLocal,
  SOVQueryChannelsState,
  SOVQuerySearchResultsEndpoint,
  SOVQueryStepType
} from 'modules/insights/sov/types';

const { SOV } = API.END_POINTS;

const CHANNELS_PROCESSING = 'sov/QUERY_CHANNELS_PROCESSING';
const CHANNELS_LOADED = 'sov/QUERY_CHANNELS_LOADED';
const CHANNELS_UPDATED = 'sov/QUERY_CHANNELS_UPDATED';
const CHANNELS_RESET = 'sov/QUERY_CHANNELS_RESET';

const initialState = {
  channels: getDefaultChannelsMap(),
  isLoading: false
};

const setStatus = (status: boolean): Action => ({
  type: CHANNELS_PROCESSING,

  payload: {
    status
  }
});

const channelsLoaded = (channels: SOVQueryChannelsLocal, stepType: SOVQueryStepType): Action => ({
  type: CHANNELS_LOADED,

  payload: {
    channels,
    stepType
  }
});

const channelsUpdated = (channels: Array<CheckboxesListItem>, stepType: SOVQueryStepType): Action => ({
  type: CHANNELS_UPDATED,

  payload: {
    channels,
    stepType
  }
});

export const resetChannels = (): Action => ({
  type: CHANNELS_RESET
});

export const fetchChannels = (
  queryId: number | undefined | null,
  preparedResults: Partial<SOVQuerySearchResultsEndpoint>,
  stepType: SOVQueryStepType
): ThunkAction => async (dispatch: Dispatch, getState: GetState) => {
  dispatch(setStatus(true));

  try {
    const url = SOV.getChannels();
    const response = await post(url, { data: preparedResults });
    const channels = response.data || {};
    const mergedQueries = getState().sovQueries.queriesMerged;
    let updatedChannels = channels;

    if (queryId) {
      const activeQuery = getActiveQuery(queryId, mergedQueries);
      const activeChannels = activeQuery.channels?.[stepType];

      updatedChannels = channels.map(item => ({
        ...item,
        selected: activeChannels?.general.some(channel => channel.keyword === item.keyword)
      }));
    }

    const convertedResults = updatedChannels.map(convertItemToCheckbox);

    dispatch(setStatus(false));

    return dispatch(channelsLoaded(convertedResults, stepType));
  } catch (error) {
    logError(error);
    throw error;
  } finally {
    dispatch(setStatus(false));
  }
};

export const updateNonFilteredChannels = (
  item: CheckboxesListItem,
  stepType: SOVQueryStepType,
  statusOverride?: boolean | null
): ThunkAction => (dispatch: Dispatch, getState: GetState) => {
  const activeChannels = getState().sovQueriesChannels.channels[stepType];
  const updatedChannels = activeChannels.map(channel => {
    let status = channel.isSelected;

    if (isNil(statusOverride) && channel.id === item.id) {
      status = !channel.isSelected;
    }

    return {
      ...channel,
      isSelected: isBoolean(statusOverride) ? statusOverride : status
    };
  });

  return dispatch(channelsUpdated(updatedChannels, stepType));
};


export const updateFilteredChannels = (filteredChannels: Array<CheckboxesListItem>, stepType: SOVQueryStepType): ThunkAction => (dispatch: Dispatch, getState: GetState) => {
  const shouldDeselectAll = filteredChannels.every(item => item.isSelected);
  const activeChannels = getState().sovQueriesChannels.channels[stepType];
  const updatedItems = activeChannels.map(item => {
    if (shouldDeselectAll) {
      return {
        ...item,
        isSelected: false
      };
    }

    const itemFound = filteredChannels.some(channel => channel.id === item.id);

    return {
      ...item,
      isSelected: itemFound || item.isSelected
    };
  });

  return dispatch(channelsUpdated(updatedItems, stepType));
};

const handleProcessing = (state: SOVQueryChannelsState, { status }): SOVQueryChannelsState => ({
  ...state,
  isLoading: status
});

const handleLoaded = (state: SOVQueryChannelsState, { channels, stepType }): SOVQueryChannelsState => ({
  ...state,

  channels: {
    ...state.channels,
    [String(stepType)]: channels
  },

  isLoading: false
});

const handleUpdated = (state: SOVQueryChannelsState, { channels, stepType }): SOVQueryChannelsState => ({
  ...state,

  channels: {
    ...state.channels,
    [String(stepType)]: channels
  }
});

export default createReducer<SOVQueryChannelsState, any>('sovQueriesChannels', initialState, {
  [RESET_ON_LOGOUT]: () => initialState,
  [CHANNELS_RESET]: () => initialState,
  [CHANNELS_PROCESSING]: handleProcessing,
  [CHANNELS_LOADED]: handleLoaded,
  [CHANNELS_UPDATED]: handleUpdated
});
